import { GridColDef } from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getModelLabel } from '../config/modelsMap';
import { useLogsColumns } from '../hooks/useLogsColumns';
import VersionDialog from './VersionDialog';
import { logService } from '../api/log.service';

type Props = {
  modelName: string;
  objectId: string;
  onMetaChange?: (meta: { hasDeleteVersion: boolean; title: string; subtitle: string }) => void;
};

export const ObjectVersionsPanel = (props: Props) => {
  const { modelName, objectId, onMetaChange } = props;
  const { t } = useTranslation();
  const datagridRefresh = useRef(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<number>(null);
  const [hasDeleteVersion, setHasDeleteVersion] = useState<boolean>(false);

  const handleOpenDialog = (id: number) => {
    setSelectedVersion(id);
    setDialogOpen(true);
  };

  const columns: GridColDef[] = useLogsColumns(handleOpenDialog);

  const titleKey = hasDeleteVersion
    ? 'pages.versions.deleted.view.title'
    : 'pages.versions.object.view.title';

  const title = t(titleKey);
  const subtitle = `${getModelLabel(modelName)} id: ${objectId}`;

  useEffect(() => {
    if (!onMetaChange) return;
    onMetaChange({ hasDeleteVersion, title, subtitle });
  }, [hasDeleteVersion, title, subtitle, onMetaChange]);

  return (
    <>
      <EnhancedDataGrid
        columns={columns}
        service={logService}
        refresh={datagridRefresh}
        outerParams={{ object_id: objectId, model_name: modelName }}
        onDataLoaded={(items) => {
          const hasDelete = items.some((row) =>
            String(row?.action ?? '')
              .toLowerCase()
              .includes('delete')
          );
          setHasDeleteVersion(hasDelete);
        }}
      />
      {selectedVersion && (
        <VersionDialog
          open={dialogOpen}
          onClose={() => {
            setSelectedVersion(null);
            setDialogOpen(false);
          }}
          versionId={selectedVersion}
        />
      )}
    </>
  );
};

export default ObjectVersionsPanel;
